/*
  Product listing: behaviour of the collection page and of the search results
  page, which is laid out like it. Loaded by kv-collection-listing.
*/
// Progressive enhancement for the collection page. Everything below is
// optional: without it the form is a plain GET form and the links are plain
// links, so filtering, sorting and paging still work with a full reload.
if (!customElements.get('kv-collection')) {
  const DEBOUNCE_MS = 600;

  // Matches the stylesheet: from this width the filters sit in the page
  // instead of in the drawer, unless the drawer is the chosen position.
  const WIDE_QUERY = '(min-width: 1025px)';

  // Reads a money string back into a number. `money_without_currency` follows
  // the shop's locale, so the separator that appears last is the decimal one.
  function parseAmount(raw) {
    if (!raw) return null;
    const cleaned = String(raw).replace(/[^\d.,]/g, '');
    if (!cleaned) return null;

    const lastDot = cleaned.lastIndexOf('.');
    const lastComma = cleaned.lastIndexOf(',');
    let normalized = cleaned;

    if (lastComma > lastDot) {
      normalized = cleaned.replace(/\./g, '').replace(',', '.');
    } else if (lastDot > -1) {
      normalized = cleaned.replace(/,/g, '');
    }

    const value = parseFloat(normalized);
    return Number.isNaN(value) ? null : value;
  }

  class KvCollection extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.results = this.querySelector('.kv-collection__results');
      this.controller = null;
      this.moreController = null;
      this.moreObserver = null;
      this.timer = null;

      // The address the results on screen belong to. A popstate that only
      // changes the #hash (a skip link, an in-page anchor) leaves it alone.
      this.renderedLocation = window.location.pathname + window.location.search;

      this.onInput = this.onInput.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.onWideChange = this.onWideChange.bind(this);
      this.onFieldKeyDown = this.onFieldKeyDown.bind(this);

      this.addEventListener('input', this.onInput);
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('keydown', this.onFieldKeyDown);
      window.addEventListener('popstate', this.onPopState);

      this.wideQuery = window.matchMedia(WIDE_QUERY);
      this.wideQuery.addEventListener('change', this.onWideChange);

      // Long lists fold only once the script can unfold them again.
      this.classList.add('is-enhanced');

      this.positionPriceBars();
      this.showNewestFilters();
      this.watchMore();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
      this.wideQuery.removeEventListener('change', this.onWideChange);
      clearTimeout(this.timer);
      if (this.moreObserver) this.moreObserver.disconnect();
      this.closeDrawer();
    }

    /* ----- events ----- */

    onChange(event) {
      const field = event.target;
      if (!this.isFilterField(field)) return;

      if (field.matches('input[type="checkbox"], input[type="radio"], select')) {
        this.submitForm();
      } else if (field.matches('[data-kv-price-input]')) {
        this.syncSlidersFromInputs(field.closest('[data-kv-price]'));
      }
    }

    // Moving the price slider or typing a price only updates the range shown
    // next to the filter button; the button (or Enter) applies it. Other
    // controls apply as soon as they change.
    onInput(event) {
      const field = event.target;
      if (!this.isFilterField(field)) return;

      if (field.matches('[data-kv-price-slider]')) {
        this.syncInputsFromSliders(field.closest('[data-kv-price]'), field);
        return;
      }

      if (field.matches('[data-kv-price-input]')) {
        this.syncSlidersFromInputs(field.closest('[data-kv-price]'));
        return;
      }

      if (field.matches('.kv-collection__text-input')) {
        this.submitFormLater();
      }
    }

    // Cards in the grid submit their own add to cart forms, which bubble up
    // through this element too; only the filter form is handled here.
    onSubmit(event) {
      if (!this.isFilterField(event.target)) return;
      event.preventDefault();
      this.submitForm(true);

      // The drawer's Apply button and the price filter's button mean "show
      // me the results", so the drawer gets out of the way and focus goes
      // back to the button that opened it.
      const submitter = event.submitter;
      if (
        this.classList.contains('is-filters-open') &&
        submitter &&
        submitter.closest('.kv-collection__filters-foot, [data-kv-price-apply]')
      ) {
        this.closeDrawer();
        const toggle = this.querySelector('[data-kv-collection-open]');
        if (toggle) toggle.focus();
      }
    }

    // Enter on a price slider applies the range, as it does in the typing
    // fields. Browsers differ on whether Enter in a slider submits the form,
    // so it is done here, through the price filter's own button.
    onFieldKeyDown(event) {
      if (event.key !== 'Enter' || event.defaultPrevented) return;
      const slider = event.target.closest && event.target.closest('[data-kv-price-slider]');
      if (!slider || !this.isFilterField(slider)) return;
      event.preventDefault();
      const apply = slider.closest('[data-kv-price]').querySelector('[data-kv-price-apply]');
      if (apply) slider.form.requestSubmit(apply);
    }

    // True for the filter form itself and for any control that belongs to it.
    // The wishlist and compare toggles on a card are checkboxes as well, and
    // ticking one must not reload the results.
    isFilterField(element) {
      const form = element.matches('form') ? element : element.form;
      return Boolean(form && form.matches('[data-kv-collection-form]'));
    }

    // Listened for on the document while the drawer is open, so Escape still
    // works after a filter change has replaced the field that had focus.
    onKeyDown(event) {
      if (event.key !== 'Escape' || !this.classList.contains('is-filters-open')) return;
      this.closeDrawer();
      const toggle = this.querySelector('[data-kv-collection-open]');
      if (toggle) toggle.focus();
    }

    // Widening past the drawer breakpoint puts the filters back in the page.
    // A drawer left open there would keep the page from scrolling.
    onWideChange(event) {
      if (event.matches && !this.classList.contains('kv-collection--drawer')) {
        this.closeDrawer();
      }
    }

    onClick(event) {
      if (event.target.closest('[data-kv-collection-open]')) {
        event.preventDefault();
        this.openDrawer();
        return;
      }

      if (event.target.closest('[data-kv-collection-close]')) {
        event.preventDefault();
        this.closeDrawer();
        return;
      }

      // With folding turned off the group headings are plain headings: a
      // click (or Enter on the focused heading, which arrives as a click)
      // must not close the group.
      if (this.dataset.collapsible === 'false' && event.target.closest('.kv-collection__filter-summary')) {
        event.preventDefault();
        return;
      }

      const toggle = event.target.closest('[data-kv-collection-toggle-values]');
      if (toggle) {
        event.preventDefault();
        this.setValuesExpanded(toggle.closest('.kv-collection__filter'), toggle.getAttribute('aria-expanded') !== 'true');
        return;
      }

      const more = event.target.closest('[data-kv-collection-more]');
      if (more && more.href) {
        event.preventDefault();
        this.loadMore(more);
        return;
      }

      const link = event.target.closest('[data-kv-collection-link]');
      if (link && link.href) {
        event.preventDefault();
        // A new page of results starts at its top, as it would after a
        // full page load.
        this.renderFrom(new URL(link.href), true, Boolean(link.closest('.kv-collection__pagination')));
      }
    }

    onPopState() {
      if (window.location.pathname + window.location.search === this.renderedLocation) return;
      this.renderFrom(new URL(window.location.href), false);
    }

    /* ----- drawer ----- */

    openDrawer() {
      this.classList.add('is-filters-open');
      document.body.classList.add('kv-collection-drawer-open');
      document.addEventListener('keydown', this.onKeyDown);
      this.setDrawerState(true);
    }

    closeDrawer() {
      this.classList.remove('is-filters-open');
      document.body.classList.remove('kv-collection-drawer-open');
      document.removeEventListener('keydown', this.onKeyDown);
      this.setDrawerState(false);
    }

    setDrawerState(open) {
      const toggle = this.querySelector('[data-kv-collection-open]');
      if (toggle) toggle.setAttribute('aria-expanded', String(open));
    }

    /* ----- filters in use ----- */

    // On a narrow screen the filters in use sit on one line that scrolls
    // sideways. It opens at its end, where the value picked last is.
    showNewestFilters() {
      const line = this.querySelector('.kv-collection__active-scroll');
      if (line && line.scrollWidth > line.clientWidth) line.scrollLeft = line.scrollWidth;
    }

    /* ----- long lists ----- */

    // Folding a long list back up would pull the page out from under the
    // button that was just pressed, so the page is scrolled by the same
    // amount and the button stays where it was.
    setValuesExpanded(group, expanded) {
      if (!group) return;
      const toggle = group.querySelector('[data-kv-collection-toggle-values]');
      const before = !expanded && toggle ? toggle.getBoundingClientRect().top : null;

      group.classList.toggle('is-expanded', expanded);
      if (toggle) toggle.setAttribute('aria-expanded', String(expanded));

      if (before !== null) {
        const shift = toggle.getBoundingClientRect().top - before;
        if (shift) window.scrollBy(0, shift);
      }
    }

    /* ----- price slider ----- */

    priceBounds(widget) {
      const max = parseFloat(widget.dataset.priceMax) || 0;
      return {
        max: max,
        minSlider: widget.querySelector('[data-kv-price-slider="min"]'),
        maxSlider: widget.querySelector('[data-kv-price-slider="max"]'),
        minInput: widget.querySelector('[data-kv-price-input="min"]'),
        maxInput: widget.querySelector('[data-kv-price-input="max"]'),
        bar: widget.querySelector('[data-kv-price-range]'),
        fromText: widget.querySelector('[data-kv-price-from]'),
        toText: widget.querySelector('[data-kv-price-to]'),
        symbol: widget.dataset.currencySymbol || '',
      };
    }

    // The range shown next to the filter button, in whole amounts.
    showPriceRange(p, from, to) {
      if (p.fromText) p.fromText.textContent = p.symbol + Math.round(from);
      if (p.toText) p.toText.textContent = p.symbol + Math.round(to);
    }

    // The amounts in the submitted fields, clamped to the slider's range.
    // A field can hold an amount between two slider steps (typed, or from a
    // link), so it - not the snapped slider - is the source of truth.
    fieldAmounts(p) {
      const typedFrom = parseAmount(p.minInput.value);
      const typedTo = parseAmount(p.maxInput.value);
      return {
        from: Math.max(0, Math.min(typedFrom === null ? 0 : typedFrom, p.max)),
        to: typedTo === null ? p.max : Math.max(0, Math.min(typedTo, p.max)),
      };
    }

    // Slider moved: only the end that moved is written into its field; the
    // other end keeps its exact amount unless the two thumbs cross.
    syncInputsFromSliders(widget, moved) {
      if (!widget) return;
      const p = this.priceBounds(widget);
      let { from, to } = this.fieldAmounts(p);

      if (moved === p.minSlider) {
        from = parseFloat(p.minSlider.value);
        if (from > to) {
          to = from;
          p.maxSlider.value = String(to);
          p.maxInput.value = to < p.max ? String(to) : '';
        }
        p.minInput.value = from > 0 ? String(from) : '';
      } else {
        to = parseFloat(p.maxSlider.value);
        if (to < from) {
          from = to;
          p.minSlider.value = String(from);
          p.minInput.value = from > 0 ? String(from) : '';
        }
        p.maxInput.value = to < p.max ? String(to) : '';
      }

      this.raiseMinThumb(p, moved === p.minSlider);
      this.paintPriceBar(widget, parseFloat(p.minSlider.value), parseFloat(p.maxSlider.value));
      this.showPriceRange(p, from, to);
    }

    // Field typed into, or the page just drawn: move the thumbs to match.
    syncSlidersFromInputs(widget) {
      if (!widget) return;
      const p = this.priceBounds(widget);
      const { from, to } = this.fieldAmounts(p);

      p.minSlider.value = String(Math.round(from));
      p.maxSlider.value = String(Math.round(to));
      this.raiseMinThumb(p, from >= to && from > 0);
      this.paintPriceBar(widget, parseFloat(p.minSlider.value), parseFloat(p.maxSlider.value));
      this.showPriceRange(p, from, to);
    }

    // The two sliders lie on top of each other. When their thumbs meet, the
    // lower one must stay reachable: it is brought to the front when it was
    // the one moved last, or when both ends sit together above nothing.
    raiseMinThumb(p, raise) {
      p.minSlider.classList.toggle('is-front', Boolean(raise));
    }

    paintPriceBar(widget, from, to) {
      const p = this.priceBounds(widget);
      if (!p.bar || !p.max) return;
      const left = Math.max(0, Math.min((from / p.max) * 100, 100));
      const right = Math.max(0, Math.min((to / p.max) * 100, 100));
      p.bar.style.left = left + '%';
      p.bar.style.width = Math.max(0, right - left) + '%';
    }

    positionPriceBars() {
      this.querySelectorAll('[data-kv-price]').forEach((widget) => {
        this.syncSlidersFromInputs(widget);
      });
    }

    /* ----- load more / infinite scroll ----- */

    // In infinite mode the next page is fetched as soon as its link comes
    // within a screen of the viewport. The link stays a real link, so a
    // shopper can still click it if the observer is not supported.
    watchMore() {
      if (this.moreObserver) this.moreObserver.disconnect();
      const more = this.querySelector('[data-kv-collection-more="infinite"]');
      if (!more || !('IntersectionObserver' in window)) return;

      this.moreObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.loadMore(more);
        },
        { rootMargin: '0px 0px 100% 0px' }
      );
      this.moreObserver.observe(more);
    }

    loadMore(link) {
      if (link.getAttribute('aria-busy') === 'true') return;
      const grid = this.querySelector('.kv-collection__grid');
      const wrap = link.closest('[data-kv-collection-more-wrap]');
      if (!grid || !wrap) return;

      const fetchUrl = new URL(link.href);
      fetchUrl.searchParams.set('section_id', this.sectionId);

      link.setAttribute('aria-busy', 'true');
      this.moreController = new AbortController();

      fetch(fetchUrl.toString(), { signal: this.moreController.signal })
        .then((response) => {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const freshGrid = doc.querySelector('.kv-collection__grid');
          const freshWrap = doc.querySelector('[data-kv-collection-more-wrap]');

          // An answer without products is an error page, not the last page:
          // the last page still has its products, it just has no link on.
          if (!freshGrid) throw new Error('No products in the response');
          Array.from(freshGrid.children).forEach((item) => grid.appendChild(item));

          if (freshWrap) {
            wrap.replaceWith(freshWrap);
          } else {
            wrap.remove();
          }

          this.watchMore();
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          // Fall back to opening the next page, so the shopper is never stuck.
          window.location.assign(link.href);
        });
    }

    /* ----- fetching ----- */

    submitFormLater() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.submitForm(), DEBOUNCE_MS);
    }

    // `applyPrice` is true for an explicit submit (a button, or Enter). Any
    // other change - ticking a brand, sorting - keeps the price range that is
    // already applied, not one that was dragged and never confirmed. The
    // price fields' default values always hold the applied range, because the
    // whole form is redrawn from the server after every change.
    submitForm(applyPrice) {
      clearTimeout(this.timer);
      const form = this.querySelector('[data-kv-collection-form]');
      if (!form) return;

      // Empty fields are dropped so an untouched filter adds nothing to the
      // URL, and `page` is left out so a new filter starts at page one. The
      // collection's own order is left out too, as the page opens in it anyway.
      const sort = form.querySelector('[data-kv-collection-sort]');
      const defaultSort = sort ? sort.dataset.defaultSort : '';
      const pending = {};
      if (!applyPrice) {
        form.querySelectorAll('[data-kv-price-input]').forEach((field) => {
          pending[field.name] = field.defaultValue;
        });
      }

      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        const sent = Object.prototype.hasOwnProperty.call(pending, key) ? pending[key] : value;
        if (sent === '') return;
        if (key === 'sort_by' && sent === defaultSort) return;
        params.append(key, sent);
      });

      const target = new URL(window.location.href);
      target.search = params.toString();
      this.renderFrom(target);
    }

    renderFrom(target, updateHistory, scrollToResults) {
      const fetchUrl = new URL(target.href);
      fetchUrl.searchParams.set('section_id', this.sectionId);

      if (this.controller) this.controller.abort();
      if (this.moreController) this.moreController.abort();
      this.controller = new AbortController();
      this.setLoading(true);

      fetch(fetchUrl.toString(), { signal: this.controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then((html) => {
          const fresh = new DOMParser()
            .parseFromString(html, 'text/html')
            .getElementById('KvCollectionResults-' + this.sectionId);
          if (!fresh) throw new Error('No results in the response');

          this.swapResults(fresh);
          this.setLoading(false);
          this.renderedLocation = target.pathname + target.search;

          if (updateHistory !== false) {
            window.history.pushState({}, '', target.href);
          }

          if (scrollToResults) this.scrollToResults();
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          this.setLoading(false);
          // Fall back to a normal page load so the shopper is never stuck.
          window.location.assign(target.href);
        });
    }

    // Puts the new results in place without undoing what the shopper was in
    // the middle of: which groups were unfolded, which long lists were shown
    // in full, how far the drawer's list was scrolled, and the field that had
    // focus - with whatever had been typed into it since the request went out.
    swapResults(fresh) {
      const groups = {};
      const expanded = {};
      this.querySelectorAll('.kv-collection__filter').forEach((group) => {
        groups[group.dataset.filterKey] = group.open;
        expanded[group.dataset.filterKey] = group.classList.contains('is-expanded');
      });

      const list = this.querySelector('.kv-collection__filters-body');
      const listScroll = list ? list.scrollTop : 0;

      // A price range dragged or typed while the request was out, and not
      // applied yet, is carried over rather than snapped back.
      const pendingPrices = {};
      this.querySelectorAll('[data-kv-price-input]').forEach((field) => {
        if (field.id && parseAmount(field.value) !== parseAmount(field.defaultValue)) {
          pendingPrices[field.id] = field.value;
        }
      });

      const active = document.activeElement;
      let focus = null;
      if (active && active.id && this.results.contains(active)) {
        const typed = active.matches('input[type="text"]');
        focus = {
          id: active.id,
          value: typed ? active.value : null,
          start: typed ? active.selectionStart : null,
          end: typed ? active.selectionEnd : null,
        };
      }

      this.results.innerHTML = fresh.innerHTML;

      this.results.querySelectorAll('.kv-collection__filter').forEach((group) => {
        if (Object.prototype.hasOwnProperty.call(groups, group.dataset.filterKey)) {
          group.open = groups[group.dataset.filterKey];
        }
        if (expanded[group.dataset.filterKey]) this.setValuesExpanded(group, true);
      });

      const freshList = this.querySelector('.kv-collection__filters-body');
      if (freshList) freshList.scrollTop = listScroll;

      Object.keys(pendingPrices).forEach((id) => {
        const field = document.getElementById(id);
        if (field) field.value = pendingPrices[id];
      });

      if (focus) {
        const field = document.getElementById(focus.id);
        if (field) {
          if (focus.value !== null) {
            field.value = focus.value;
            field.setSelectionRange(focus.start, focus.end);
          }
          field.focus({ preventScroll: true });
        }
      }

      this.setDrawerState(this.classList.contains('is-filters-open'));
      this.positionPriceBars();
      this.showNewestFilters();
      this.watchMore();
    }

    // Brings the top of the results into view, below anything pinned to the
    // top of the screen (a sticky header), when it has scrolled out of sight.
    scrollToResults() {
      const top = this.results.getBoundingClientRect().top;
      if (top >= 0) return;

      let pinned = 0;
      document.elementsFromPoint(window.innerWidth / 2, 1).forEach((element) => {
        for (let node = element; node && node !== document.body; node = node.parentElement) {
          const position = getComputedStyle(node).position;
          if (position === 'fixed' || position === 'sticky') {
            pinned = Math.max(pinned, node.getBoundingClientRect().bottom);
            break;
          }
        }
      });

      window.scrollTo({ top: window.scrollY + top - pinned });
    }

    setLoading(loading) {
      const grid = this.querySelector('.kv-collection__grid');
      const toolbar = this.querySelector('.kv-collection__toolbar');
      if (grid) grid.classList.toggle('is-loading', loading);
      if (toolbar) toolbar.classList.toggle('is-loading', loading);
    }
  }

  customElements.define('kv-collection', KvCollection);
}
